import {
  FormControl,
  InputAdornment,
  Typography,
  FilledInput,
  FormHelperText,
  CircularProgress,
  Link,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { AddressTestingStyles } from "../common/styles/AddressTesting.styles";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import clsx from "clsx";
import api from "src/store/features/api";
import { useVerfiyUserWithdrawAddressEmailMutation } from "src/store/features/api/generated";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useAddressTestingState } from "src/store/features/addressTesting.slice";
import { trackEvent } from "src/Amplitude";
import { truncateEmail } from "src/utils/helpers/EmailTruncate";

type Props = {
  submitAddress: ReturnType<typeof useVerfiyUserWithdrawAddressEmailMutation>[0];
  setEmailCode: React.Dispatch<React.SetStateAction<string>>;
  setTwoFactorCode: React.Dispatch<React.SetStateAction<string>>;
  twoFactorIsError: boolean;
  twoFactorError: string;
  twoFactorVerified: boolean;
  setEmailVerified: React.Dispatch<React.SetStateAction<boolean>>;
  emailVerified: boolean;
  setStep: ActionCreatorWithPayload<number, string>;
  emailCode: string;
  email?: string;
  setTwoFactorIsError: React.Dispatch<React.SetStateAction<boolean>>;
  loading2fa: boolean;
  twoFactorCode: string;
  address: string;
};

const AddressVerify = (props: Props) => {
  const classes = AddressTestingStyles();
  const {
    twoFactorIsError,
    twoFactorError,
    loading2fa,
    setTwoFactorIsError,
    setEmailVerified,
    emailVerified,
  } = props;
  // --------------------------RTKQ--------------------------
  // Address is used if the user continues flow from table
  const { address, selectedCurrency } = useAddressTestingState();
  const [
    checkEmailCode,
    {
      data: checkEmailData,
      error: checkEmailError,
      isError: checkEmailIsError,
      isSuccess: checkEmailSuccess,
      isLoading: emailCheckLoading,
      reset: resetEmail,
    },
  ] = api.useCheckEmailCodeMutation();
  const [
    resendEmail,
    {
      error: resendEmailError,
      isError: resendEmailIsError,
      isSuccess: resendEmailSuccess,
      isLoading: emailresendLoading,
      reset: resetSendEmail,
    },
  ] = api.useResendEmailCodeMutation();
  //---------------------------STATE-------------------------
  const [showInitialMessage, setShowInitialMessage] = useState(true);
  const [checkEmailErrorMessage, setCheckEmailErrorMessage] = useState("");
  const [resendEmailErrorMessage, setResendEmailErrorMessage] = useState("");

  //---------------------------Consts-------------------------
  const SUB_TITLE = "We sent a verification code to your registered email address " as const;

  //---------------------------Handlers-----------------------

  const handleEmailCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // reset the rtkq error messages if backspaced
    setEmailVerified(false);

    // Remove any non-numeric characters
    const inputCode = event.target.value.replace(/[^0-9]/g, "");
    if (inputCode?.length <= 6) {
      props.setEmailCode(inputCode);
    }

    setShowInitialMessage(false);
    if (event.target.value.length === 6) {
      trackEvent("Address_Testing_2fa_Entered");
      checkEmailCode({
        addressWithEmailCodeDto: {
          emailCode: inputCode,
          address: props.address || address,
        },
      })
        .unwrap()
        .then((resp) => {
          // If the operation was successful, clear the error message
          setCheckEmailErrorMessage("");
          setEmailVerified(true);
        })
        .catch((error) => {
          let errorMessage = "An unexpected error occurred.";
          if (error && typeof error.message === "string") {
            errorMessage = error.message;
          }

          setCheckEmailErrorMessage(errorMessage);
        });
    }
  };

  const handleResend2FACode = async () => {
    if (selectedCurrency) {
      await resendEmail({ resendEmailCodeDto: { address, token: selectedCurrency.code } });
    }
  };

  useEffect(() => {
    if (resendEmailIsError) {
      setResendEmailErrorMessage(`${resendEmailError?.message ?? "Failed to resend email"}`);
    }
  }, [resendEmailError, resendEmailIsError]);

  useEffect(() => {
    if (!checkEmailError && checkEmailSuccess) {
      setEmailVerified(true);
      setCheckEmailErrorMessage(""); // clear error message
    } else if (checkEmailIsError) {
      setCheckEmailErrorMessage(checkEmailError?.message || "An unexpected error occurred.");
    } else if (showInitialMessage) {
      setCheckEmailErrorMessage("Please enter verification code above to proceed");
    }
  }, [checkEmailData, showInitialMessage]);

  const handleTwoFactorCodeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-numeric characters
    const inputCode = event.target.value.replace(/[^0-9]/g, "");
    if (inputCode?.length <= 6) {
      props.setTwoFactorCode(inputCode);
    }
  };

  useEffect(() => {
    if (!checkEmailError && checkEmailSuccess) {
      setEmailVerified(true);
    }

    return () => {
      setTwoFactorIsError(false);
      resetEmail();
      props.setTwoFactorCode("");
    };
  }, [checkEmailData]);

  //---------------------------Helper Components-----------------------
  const Resend2FACodeHelperText = () => {
    useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      // if resending email was successful. reset the states
      if (resendEmailSuccess) {
        timeoutId = setTimeout(() => {
          resetSendEmail();
        }, 5000);
      }

      return () => {
        clearTimeout(timeoutId);
      };
    }, [resendEmailIsError, resendEmailSuccess]);

    const renderContent = () => {
      if (emailresendLoading) {
        return <CircularProgress size={10} />;
      } else if (resendEmailIsError) {
        return (
          <>
            {resendEmailErrorMessage}{" "}
            <Link
              className={classes.helperTextLink}
              component="button"
              onClick={handleResend2FACode}>
              try again.
            </Link>
            .
          </>
        );
      } else if (resendEmailSuccess) {
        return <>Email sent</>;
      } else {
        return (
          <>
            If you did not receive this code, please{" "}
            <Link
              className={classes.helperTextLink}
              component="button"
              onClick={handleResend2FACode}>
              click here
            </Link>{" "}
            to request a new code.
          </>
        );
      }
    };

    return <FormHelperText className={classes.resend2facode}>{renderContent()}</FormHelperText>;
  };

  return (
    <div>
      <Typography className={classes.securityNotice}>
        As an added layer of security, we have a 2 step verification that you will need to complete
        to proceed with your address creation.
      </Typography>
      <FormControl className={classes.formContainer}>
        <Typography className={classes.stepHeader}>Step 1: Email verification</Typography>
        <Typography className={classes.explainerText} sx={{ fontSize: "14px", lineHeight: "17px" }}>
          {SUB_TITLE}
          <span style={{ fontWeight: 600 }}>{truncateEmail(props.email)}</span>
        </Typography>
        <div className={classes.inputWrapper}>
          <FilledInput
            type="standard"
            id="crypto-address-input"
            className={classes.inputFilledContainers}
            placeholder="Enter code here"
            onChange={handleEmailCodeChange}
            disableUnderline
            autoComplete="off"
            value={props.emailCode || ""}
            aria-label="Email 2fa code"
            aria-describedby={checkEmailIsError ? "email-code-error" : undefined}
            aria-invalid={checkEmailIsError}
          />
          <InputAdornment position="end" sx={{ paddingRight: 2 }}>
            {emailCheckLoading ? (
              <CircularProgress size={20} />
            ) : emailVerified ? (
              <CheckCircleIcon className={classes.verifiedIcon} />
            ) : null}
            {checkEmailIsError && (
              <ErrorOutlineOutlinedIcon className={classes.errorIconInputBox} />
            )}
          </InputAdornment>
        </div>
        <FormHelperText
          className={classes.verificationHelperText}
          error={checkEmailIsError || showInitialMessage}>
          {checkEmailIsError || showInitialMessage ? (
            <>
              <ErrorOutlineOutlinedIcon className={classes.errorIcon} />
              {checkEmailErrorMessage}
            </>
          ) : null}
        </FormHelperText>
        <Resend2FACodeHelperText />
      </FormControl>
      <FormControl
        className={clsx(classes.formContainer, {
          [classes.disabled]: !emailVerified,
        })}>
        <Typography className={classes.stepHeader}>Step 2. Two factor authentication</Typography>
        <Typography className={classes.explainerText} sx={{ fontSize: "14px", lineHeight: "17px" }}>
          Enter the 2FA code from your authenticator
        </Typography>
        <div className={classes.inputWrapper}>
          <FilledInput
            type="standard"
            id="2fa-input"
            className={classes.inputFilledContainers}
            placeholder="Enter code here"
            onChange={handleTwoFactorCodeChange}
            disabled={!emailVerified}
            disableUnderline
            autoComplete="off"
            value={props.twoFactorCode || ""}
            aria-label="Two-factor authentication code"
            aria-describedby={twoFactorIsError ? "2fa-code-error" : undefined}
            aria-invalid={twoFactorIsError}
          />
          <InputAdornment position="end" sx={{ paddingRight: 2 }}>
            {loading2fa ? <CircularProgress size={20} /> : null}
            {twoFactorIsError && <ErrorOutlineOutlinedIcon className={classes.errorIconInputBox} />}
          </InputAdornment>
        </div>
        <FormHelperText className={classes.formHelperText} error={twoFactorIsError}>
          {twoFactorIsError && (
            <>
              <ErrorOutlineOutlinedIcon className={classes.errorIcon} />
              {twoFactorError}
            </>
          )}
        </FormHelperText>
      </FormControl>
    </div>
  );
};

export default AddressVerify;
